/**
 * CREATIVE DETONATOR - Credit Animation
 * Animates the existing credit counter - number counts down, coin bounces
 */

import { Sounds } from './sounds.js';
import { apiRequest } from '../api/client.js';

/**
 * Animate credit spend - counts down the number in the credit display
 */
export function animateCoinSpend(amount) {
  if (!amount || amount <= 0) return;

  // Play sound
  Sounds.play('coinSpend');

  // Find credit displays
  const displays = document.querySelectorAll('.credits-value, .credits-count, .credits-balance-value');

  displays.forEach(el => {
    const currentValue = parseInt(el.textContent) || 0;
    const newValue = Math.max(0, currentValue - amount);

    // Animate count down
    animateNumber(el, currentValue, newValue);

    // Bounce the coin icon if there's one nearby
    const coin = el.parentElement?.querySelector('.pixel-coin-static, .pixel-coin-spin');
    if (coin) {
      coin.classList.add('coin-bounce');
      setTimeout(() => coin.classList.remove('coin-bounce'), 400);
    }

    // Flash the container
    const container = el.closest('.profile-credits, .credits-widget, .credits-display');
    if (container) {
      container.classList.add('credit-pulse');
      setTimeout(() => container.classList.remove('credit-pulse'), 300);
    }
  });
}

/**
 * Animate number counting down
 */
function animateNumber(el, from, to) {
  const duration = 400;
  const steps = Math.min(Math.abs(from - to), 10);
  const stepTime = duration / steps;

  let current = from;
  const decrement = Math.ceil((from - to) / steps);

  const tick = () => {
    current -= decrement;
    if (current <= to) {
      el.textContent = to;
      return;
    }
    el.textContent = current;
    setTimeout(tick, stepTime);
  };

  tick();
}

// Legacy alias
export function animateDeduction(amount) {
  animateCoinSpend(amount);
}

/**
 * Animate credit addition
 */
export function animateAddition(amount) {
  if (!amount || amount <= 0) return;

  Sounds.play('coin');

  const displays = document.querySelectorAll('.credits-value, .credits-count, .credits-balance-value');

  displays.forEach(el => {
    const currentValue = parseInt(el.textContent) || 0;
    const newValue = currentValue + amount;

    // Animate count up
    animateNumberUp(el, currentValue, newValue);

    // Bounce coin
    const coin = el.parentElement?.querySelector('.pixel-coin-static, .pixel-coin-spin');
    if (coin) {
      coin.classList.add('coin-bounce');
      setTimeout(() => coin.classList.remove('coin-bounce'), 400);
    }
  });
}

function animateNumberUp(el, from, to) {
  const duration = 400;
  const steps = Math.min(Math.abs(to - from), 10);
  const stepTime = duration / steps;

  let current = from;
  const increment = Math.ceil((to - from) / steps);

  const tick = () => {
    current += increment;
    if (current >= to) {
      el.textContent = to;
      return;
    }
    el.textContent = current;
    setTimeout(tick, stepTime);
  };

  tick();
}

/**
 * Update credit displays (no animation)
 */
export function updateCreditDisplays(newBalance) {
  const displays = document.querySelectorAll('.credits-value, .credits-count, .credits-balance-value');
  displays.forEach(el => el.textContent = newBalance);

  const sidebar = document.querySelector('.credits-display .credits-count');
  if (sidebar) sidebar.textContent = newBalance;

  // Low credits warning
  const containers = document.querySelectorAll('.profile-credits, .credits-widget');
  containers.forEach(el => el.classList.toggle('low-credits', newBalance < 10));
}

/**
 * Insufficient credits shake
 */
export function showInsufficientCredits() {
  Sounds.play('error');

  const containers = document.querySelectorAll('.profile-credits, .credits-widget');
  containers.forEach(el => {
    el.classList.add('shake');
    setTimeout(() => el.classList.remove('shake'), 500);
  });
}

/**
 * Refresh credit balance from server and update all displays
 */
export async function refreshCreditsFromServer() {
  try {
    console.log('[credit-animation] Refreshing credits from server...');
    const response = await apiRequest('/api/credits');

    if (response.ok) {
      const data = await response.json();
      if (data.success && typeof data.credits === 'number') {
        updateCreditDisplays(data.credits);
        console.log('[credit-animation] Credits refreshed:', data.credits);
      } else {
        console.warn('[credit-animation] Invalid response:', data);
      }
    } else {
      console.warn('[credit-animation] API returned non-OK status:', response.status);
    }
  } catch (err) {
    console.warn('[credit-animation] Failed to refresh credits:', err.message);
  }
}

export const CreditAnimation = {
  animateCoinSpend,
  animateDeduction,
  animateAddition,
  updateCreditDisplays,
  showInsufficientCredits,
  refreshCreditsFromServer
};

if (typeof window !== 'undefined') {
  window.CreditAnimation = CreditAnimation;
}
